/**
 * useResponsiveChartSizes - Derives responsive chart dimensions from panel width
 *
 * Victory.js requires explicit pixel dimensions, so CSS-based sizing alone
 * (e.g. maxWidth: 100%) cannot propagate into the SVG chart. This hook
 * uses onLayout to measure the container's width, then computes chart sizes
 * that fit within the CSS Grid cell. The chart container's flex-1 parent
 * with overflow-hidden handles any vertical overflow, so only width is
 * needed to determine chart size. Using height created a feedback loop
 * (chart size → content height → panel height → chart size) that caused
 * progressive layout shifts on each metric dropdown change (SE-3829).
 */

import { useCallback, useMemo, useState } from "react";
import type { LayoutChangeEvent } from "react-native";

import {
  SELECTOR_PADDING,
  SPIDER_CHART_SIZE,
  DOT_PLOT_SIZE,
} from "@/features/player-detail/constants";
import {
  getChartDimensions,
  type ChartDimensions,
} from "@/utils/radarChartUtils";

/** Padding consumed by the p-4 Box inside the measured panel (16px each side) */
const PANEL_PADDING_TOTAL = 32;

/** Minimum chart size to keep the radar readable */
const MIN_CHART_SIZE = 160;

/**
 * Minimum width change (in pixels) to trigger a state update.
 * Prevents excessive re-renders during animated sidebar transitions
 * where ResizeObserver fires on every frame.
 */
const DIMENSION_THRESHOLD = 2;

/**
 * Return type for the useResponsiveChartSizes hook
 */
interface ResponsiveChartSizes {
  /** Layout handler to attach to the panel's onLayout prop */
  readonly handleLeftPanelLayout: (event: LayoutChangeEvent) => void;
  /** Responsive spider chart size derived from panel width (min MIN_CHART_SIZE) */
  readonly responsiveChartSize: number;
  /** Responsive dot plot size (capped at DOT_PLOT_SIZE, min MIN_CHART_SIZE) */
  readonly responsiveDotPlotSize: number;
  /** Chart dimensions derived from responsiveChartSize */
  readonly chartDimensions: ChartDimensions;
}

/**
 * Measures the left panel width and derives responsive chart sizes.
 * Only width is used for sizing to avoid a feedback loop between chart
 * content height and panel height measurements (SE-3829).
 * @param selectorPadding - Per-side padding for metric selector labels around the chart (default SELECTOR_PADDING)
 * @returns Layout handler and responsive chart sizes/dimensions
 */
export const useResponsiveChartSizes = (
  selectorPadding = SELECTOR_PADDING
): ResponsiveChartSizes => {
  const selectorPaddingTotal = selectorPadding * 2;
  const [panelWidth, setPanelWidth] = useState(0);

  const handleLeftPanelLayout = useCallback((event: LayoutChangeEvent) => {
    const { width } = event.nativeEvent.layout;
    setPanelWidth(prev =>
      Math.abs(prev - width) > DIMENSION_THRESHOLD ? width : prev
    );
  }, []);

  const responsiveChartSize = useMemo(() => {
    if (panelWidth === 0) return SPIDER_CHART_SIZE;
    const availableWidth =
      panelWidth - PANEL_PADDING_TOTAL - selectorPaddingTotal;
    return Math.max(availableWidth, MIN_CHART_SIZE);
  }, [panelWidth, selectorPaddingTotal]);

  const responsiveDotPlotSize = useMemo(() => {
    if (panelWidth === 0) return DOT_PLOT_SIZE;
    const available = panelWidth - PANEL_PADDING_TOTAL;
    return Math.max(Math.min(available, DOT_PLOT_SIZE), MIN_CHART_SIZE);
  }, [panelWidth]);

  const chartDimensions = useMemo(
    () =>
      getChartDimensions(
        responsiveChartSize,
        responsiveChartSize,
        responsiveChartSize,
        responsiveChartSize
      ),
    [responsiveChartSize]
  );

  return {
    handleLeftPanelLayout,
    responsiveChartSize,
    responsiveDotPlotSize,
    chartDimensions,
  };
};
